import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Pill, STATUS_COLORS, PRIORITY_COLORS } from './NotionUI';
import { linkProjectTaskTicket, unlinkProjectTaskTicket, searchProjectTickets, getTicketProjects, createTicket, errorMessage } from '../services/api';
import Select from './Select';
import { useLanguage } from '../i18n/LanguageContext';
import Highlight from './KernelHighlight';
import { searchTerms } from '../utils/kernelSearch';
import { fmtDateDMY } from '../utils/format';

/**
 * Ticket collegati a un'attività: elenco con collegamento alla pagina del
 * ticket, rimozione e ricerca per aggiungerne. Ogni ticket si mostra con ciò che serve a riconoscerlo
 * (coda, richiedente, gruppo, priorità, data), anche nei risultati della ricerca.
 * `onChange(tickets)` riceve l'elenco aggiornato dopo ogni modifica.
 */
/** Titolo e dettagli di un ticket: numero, oggetto, coda, richiedente, gruppo, priorità e data. */
function TicketInfo({ ticket, terms, asLink = false, from }) {
  const { t } = useLanguage();
  const idMatches = terms.some((term) => String(ticket.id) === term.replace(/^#/, ''));
  const id = <span className="tl-id">#{idMatches ? <mark className="khl">{ticket.id}</mark> : ticket.id}</span>;
  const subject = <Highlight text={ticket.subject} terms={terms} />;
  return (
    <div className="tl-info">
      <div className="tl-title">
        {asLink ? <Link to={`/tickets/${ticket.id}`} state={{ from }}>{id} {subject}</Link> : <>{id} {subject}</>}
      </div>
      <div className="tl-meta">
        {ticket.project_name && <Pill color={ticket.project_color}><Highlight text={ticket.project_name} terms={terms} /></Pill>}
        <span className="tl-req"><Highlight text={ticket.customer_email || t('tickets.requesterStaff')} terms={terms} /></span>
        {ticket.group_name && (
          <span className="cg-tag cg-tag-sm" style={{ '--pill': ticket.group_color }} title={t('tickets.customerGroup')}>
            <i className="cg-dot" aria-hidden="true" /><Highlight text={ticket.group_name} terms={terms} />
          </span>
        )}
        {ticket.priority && (
          <span className="tk-prio" style={{ '--c': PRIORITY_COLORS[ticket.priority] }}>
            <i aria-hidden="true" /> {t(`tickets.priority_${ticket.priority}`)}
          </span>
        )}
        {ticket.assignee_name && <span className="tl-req" title={t('projectDetail.ticketAssignee')}>👤 {ticket.assignee_name}</span>}
        {ticket.created_at && <span className="tl-date">{fmtDateDMY(ticket.created_at)}</span>}
      </div>
    </div>
  );
}

function TaskTicketLinks({ task, onChange, onError }) {
  const { t } = useLanguage();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const terms = searchTerms(query);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  const [queues, setQueues] = useState([]);
  const [queueId, setQueueId] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    getTicketProjects().then((data) => setQueues(data.projects || [])).catch(() => {});
  }, []);

  const linkedIds = task.tickets.map((tk) => tk.id);

  // Crea un ticket nella coda scelta, con il titolo della riga, e lo collega: la riga e il ticket restano due cose distinte.
  const createFromTask = async () => {
    if (!queueId) return;
    setCreating(true);
    try {
      const title = task.title || t('projectDetail.untitled');
      const created = await createTicket({ project_id: Number(queueId), subject: title, description: title });
      const data = await linkProjectTaskTicket(task.id, created.ticket.id);
      onChange(data.tickets);
      setQueueId('');
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.createTicketError')));
    } finally {
      setCreating(false);
    }
  };

  // Ricerca con un piccolo ritardo per non chiamare l'API a ogni tasto.
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      searchProjectTickets(query)
        .then((data) => setResults(data.tickets || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open]);

  const link = async (ticket) => {
    setOpen(false);
    setQuery('');
    try {
      const data = await linkProjectTaskTicket(task.id, ticket.id);
      onChange(data.tickets);
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  const unlink = async (ticket) => {
    try {
      const data = await unlinkProjectTaskTicket(task.id, ticket.id);
      onChange(data.tickets);
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  return (
    <div className="pd-links">
      <div className="pd-links-title">{t('projectDetail.linkedTickets')}</div>
      {task.tickets.length === 0 && <div className="nt-muted pd-link-item">{t('projectDetail.noLinkedTickets')}</div>}
      {task.tickets.map((tk) => (
        <div key={tk.id} className="pd-link-item">
          <TicketInfo ticket={tk} terms={[]} asLink from={`${location.pathname}?task=${task.id}`} />
          <Pill dot color={STATUS_COLORS[tk.status]}>{t(`tickets.status_${tk.status}`)}</Pill>
          <button type="button" className="pd-row-delete pd-option-remove" aria-label={t('projectDetail.unlinkTicket')} onClick={() => unlink(tk)}>×</button>
        </div>
      ))}
      {queues.length > 0 && (
        <div className="ticket-linked-tasks-create">
          <Select value={queueId} onChange={(e) => setQueueId(e.target.value)} aria-label={t('projectDetail.createTicketTitle')}>
            <option value="">{t('projectDetail.createTicketQueue')}</option>
            {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
          </Select>
          <button type="button" className="btn btn-secondary" disabled={!queueId || creating} onClick={createFromTask}>
            {t('projectDetail.createTicketAction')}
          </button>
        </div>
      )}
      <div className="pd-link-search">
        <input
          value={query}
          placeholder={t('projectDetail.linkTicketPlaceholder')}
          aria-label={t('projectDetail.linkTicketPlaceholder')}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        {open && (
          <>
            <div className="pd-overlay" onClick={() => setOpen(false)} />
            <div className="pd-link-results">
              {results.filter((tk) => !linkedIds.includes(tk.id)).map((tk) => (
                <button key={tk.id} type="button" className="pd-link-result" onClick={() => link(tk)}>
                  <TicketInfo ticket={tk} terms={terms} />
                  <Pill dot color={STATUS_COLORS[tk.status]}>{t(`tickets.status_${tk.status}`)}</Pill>
                </button>
              ))}
              {results.filter((tk) => !linkedIds.includes(tk.id)).length === 0 && (
                <div className="nt-muted pd-link-result">{t('projectDetail.noTicketResults')}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TaskTicketLinks;
